/**
 * LocalityHeader — thin single-line strip shown above the tagline.
 *
 * Compact by design: renders "📍 City · State · Country · Change" as
 * inline links on one row (~28 px tall) so precious viewport space
 * stays free for the actual article content. Each label is still a
 * <Link> to the corresponding location article so the drill-down UX
 * is preserved.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Edit2 } from "lucide-react";
import { useLocality } from "@/contexts/LocalityContext";
import LocalityDialog from "@/components/LocalityDialog";
import { titleCase } from "@/lib/titleCase";

function slugify(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function href({ country, state, city }) {
  const parts = [country, state, city].filter(Boolean).map(slugify);
  return "/l/" + parts.join("/");
}

export default function LocalityHeader() {
  const { locality, status } = useLocality();
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setDialogOpen(true);
    window.addEventListener("sp-open-locality-dialog", onOpen);
    return () => window.removeEventListener("sp-open-locality-dialog", onOpen);
  }, []);

  if (status === "loading") return null;

  return (
    <>
      <div
        data-testid="locality-header"
        className="max-w-6xl mx-auto px-6 py-1.5 flex items-center gap-2 text-sm"
      >
        {locality ? (
          <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 min-w-0">
            <MapPin className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--wofy-primary)" }} />
            <span className="text-xs uppercase tracking-widest opacity-60 mr-1" style={{ color: "var(--wofy-text-soft)" }}>
              You're viewing
            </span>
            {locality.city && (
              <>
                <ChipLink
                  to={href({ country: locality.country, state: locality.state, city: locality.city })}
                  label={titleCase(locality.city)}
                  testid="locality-chip-city"
                  primary
                />
                <ChipSep />
              </>
            )}
            {locality.state && (
              <>
                <ChipLink
                  to={href({ country: locality.country, state: locality.state })}
                  label={titleCase(locality.state)}
                  testid="locality-chip-state"
                />
                <ChipSep />
              </>
            )}
            {locality.country && (
              <ChipLink
                to={href({ country: locality.country })}
                label={titleCase(locality.country)}
                testid="locality-chip-country"
              />
            )}
            {locality.postal_code && (
              <>
                <ChipSep />
                <span
                  data-testid="locality-chip-postal"
                  className="font-mono text-xs opacity-70"
                  style={{ color: "var(--wofy-text-soft)" }}
                >
                  {locality.postal_code}
                </span>
              </>
            )}
            <button
              data-testid="locality-change-btn"
              onClick={() => setDialogOpen(true)}
              className="ml-2 text-xs inline-flex items-center gap-1 hover:underline"
              style={{ color: "var(--wofy-text-soft)" }}
            >
              <Edit2 className="w-3 h-3" /> Change
            </button>
          </div>
        ) : (
          <div className="flex items-center flex-wrap gap-2">
            <MapPin className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
            <button
              data-testid="locality-set-btn"
              onClick={() => setDialogOpen(true)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
              style={{ color: "var(--wofy-primary)" }}
            >
              Set your location
              <span className="text-xs opacity-70 font-normal" style={{ color: "var(--wofy-text-soft)" }}>
                — tap to personalize
              </span>
            </button>
          </div>
        )}
      </div>

      {dialogOpen && <LocalityDialog onClose={() => setDialogOpen(false)} />}
    </>
  );
}

function ChipLink({ to, label, testid, primary = false }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`hover:underline transition-colors whitespace-nowrap ${
        primary ? "font-bold" : "font-medium"
      }`}
      style={{
        color: primary ? "var(--wofy-primary)" : "var(--wofy-text)",
      }}
    >
      {label}
    </Link>
  );
}

function ChipSep() {
  return <span className="opacity-40 text-sm" style={{ color: "var(--wofy-text-soft)" }}>·</span>;
}
